// client/src/components/ComprobanteVenta.jsx
import React from "react";

const ComprobanteVenta = ({ venta, onClose }) => {
  const handlePrint = () => {
    window.print();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("es-EC", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Construir URL completa del logo usando la URL de la API
  const getLogoUrl = () => {
    if (!venta.establecimiento?.restaurante?.logo) return null;
    
    const logoPath = venta.establecimiento.restaurante.logo;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    // Si la ruta ya es completa, usarla tal cual
    if (logoPath.startsWith('http')) {
      return logoPath;
    }
    
    // Si la ruta empieza con /, construir URL completa
    return `${apiUrl}${logoPath}`;
  };
  
  const logoUrl = getLogoUrl();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Botones de acción - No se imprimen */}
        <div className="flex justify-end gap-2 p-4 border-b print:hidden">
          <button
            onClick={handlePrint}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
          >
            🖨️ Imprimir
          </button>
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Cerrar
          </button>
        </div>

        {/* Contenido del comprobante - Se imprime */}
        <div className="p-4 text-xs relative" id="comprobante-content">
          {/* Marca de agua - Logo de fondo */}
          {logoUrl && (
            <div 
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ zIndex: 0 }}
            >
              <img
                src={logoUrl}
                alt="Marca de agua"
                className="w-full h-auto object-contain opacity-10"
                style={{ maxHeight: '80%' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          {/* Contenido sobre la marca de agua */}
          <div className="relative" style={{ zIndex: 1 }}>
          {/* Encabezado - Formato Térmico Compacto */}
          <div className="text-center mb-3 pb-2 border-b border-dashed border-gray-400">
            
            
            {/* Nombre Comercial */}
            <h1 className="text-sm font-bold text-gray-800 mb-1 uppercase">
              {venta.establecimiento?.restaurante?.nombre_comercial || 
              venta.establecimiento?.restaurante?.razon_social || 
              "RESTAURANTE"}
            </h1>
            
            {/* RUC */}
            <p className="text-xs text-gray-700 font-semibold">
              RUC: {venta.establecimiento?.restaurante?.ruc || "—"}
            </p>
            
            {/* Dirección */}
            <p className="text-xs text-gray-600 leading-tight">
              {venta.establecimiento?.direstablecimiento || 
              venta.establecimiento?.restaurante?.direccion_matriz || 
              "—"}
            </p>
            
            {/* Teléfono */}
            {venta.establecimiento?.restaurante?.telefono && (
              <p className="text-xs text-gray-600">
                Tel: {venta.establecimiento.restaurante.telefono}
              </p>
            )}
          </div>

          {/* Tipo de Comprobante */}
          <div className="text-center mb-2">
            <h2 className="text-xs font-bold text-gray-800 uppercase">
              {venta.facturada ? "FACTURA ELECTRÓNICA" : "COMPROBANTE DE VENTA"}
            </h2>
            <p className="text-xs text-gray-600">
              {venta.tipo_venta === "consumo_establecimiento" ? "Consumo Local" : "Delivery"}
            </p>
          </div>

          {/* Información de la Venta */}
          <div className="mb-2 pb-2 border-b border-dashed border-gray-400">
            <div className="text-xs space-y-0.5">
              <div className="flex justify-between">
                <span className="font-semibold">N° Venta:</span>
                <span>{venta.id_venta}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Fecha:</span>
                <span>{formatDate(venta.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Pago:</span>
                <span className="capitalize">{venta.metodo_pago}</span>
              </div>
            </div>
          </div>

          {/* Información del Cliente */}
          {venta.tipo_cliente === "con_datos" && venta.cliente && (
            <div className="mb-2 pb-2 border-b border-dashed border-gray-400">
              <p className="text-xs font-bold mb-1">CLIENTE:</p>
              <div className="text-xs space-y-0.5">
                <p className="font-semibold">{venta.cliente.nombre}</p>
                <p>CI/RUC: {venta.cliente.identificacion}</p>
                {venta.cliente.telefono && <p>Tel: {venta.cliente.telefono}</p>}
                {venta.cliente.direccion && <p>Dir: {venta.cliente.direccion}</p>}
              </div>
            </div>
          )}

          {venta.tipo_cliente === "consumidor_final" && (
            <div className="mb-2 pb-2 border-b border-dashed border-gray-400 text-center">
              <p className="text-xs font-bold">CONSUMIDOR FINAL</p>
            </div>
          )}

          {/* Detalle de Productos */}
          <div className="mb-2">
            <p className="text-xs font-bold mb-1 pb-1 border-b border-gray-400">PRODUCTOS</p>
            <div className="text-xs">
              {venta.detalles?.map((detalle, index) => (
                <div key={index} className="mb-2 pb-1 border-b border-dotted border-gray-300">
                  <div className="flex justify-between font-semibold">
                    <span>{detalle.producto?.nombre || "—"}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>{detalle.cantidad} x ${Number(detalle.precio_unitario).toFixed(2)}</span>
                    <span className="font-semibold">
                      ${(Number(detalle.subtotal) + Number(detalle.iva_valor)).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totales */}
          <div className="border-t-2 border-gray-800 pt-2 mb-2">
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>${Number(venta.subtotal).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>IVA (15%):</span>
                <span>${Number(venta.impuestos).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-gray-800 pt-1">
                <span>TOTAL:</span>
                <span>${Number(venta.total).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Información adicional si está facturada */}
          {venta.facturada && venta.factura && (
            <div className="mb-2 pb-2 border-b border-dashed border-gray-400">
              <p className="text-xs font-bold mb-1">FACTURA ELECTRÓNICA</p>
              <div className="text-xs space-y-0.5">
                <p>N°: {venta.factura.numero_factura}</p>
                <p className="break-all">Clave: {venta.factura.clave_acceso}</p>
                <p>Estado: <span className="capitalize">{venta.factura.estado_factura}</span></p>
              </div>
            </div>
          )}

          {/* Pie de página */}
          <div className="text-center text-xs text-gray-600 border-t border-dashed border-gray-400 pt-2">
            <p className="font-semibold">¡Gracias por su preferencia!</p>
            <p className="mt-1 text-[10px]">
              {venta.facturada ? "Factura electrónica autorizada SRI" : "Comprobante de venta"}
            </p>
            <p className="mt-1 text-[10px]">{formatDate(new Date())}</p>
          </div>
          </div> {/* Cierre del contenido relativo */}
        </div>
      </div>

      {/* Estilos de impresión para impresora térmica */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #comprobante-content,
          #comprobante-content * {
            visibility: visible;
          }
          #comprobante-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
            max-width: 80mm;
            padding: 2mm;
            font-size: 10px;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            size: 80mm auto;
            margin: 0;
          }
          /* Asegurar que la marca de agua se imprima */
          #comprobante-content img {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
};

export default ComprobanteVenta;
